/**
 * Lender Income Shading Panel (SPRINT-6B F009)
 *
 * CRUD panel for managing lender-specific income shading rules.
 * Each rule defines how a lender shades (discounts) a specific income type
 * for serviceability calculations.
 *
 * States:
 * - Empty: "No income shading rules configured" with Add Shading Rule button
 * - Table: Columns Income Type | Borrower Type | Shading % | Conditions | Active | Actions
 * - Add Modal: "Add Shading Rule"
 * - Edit Modal: "Edit Shading Rule"
 * - Delete AlertDialog: "Delete Shading Rule"
 * - Active toggle: inline update with toast
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Loader2, Percent } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface IncomeShadingRule {
  id: string;
  lenderId: string;
  incomeType: string;
  borrowerType: string;
  shadingPercentage: number;
  minYearsEmployed: number | null;
  requiresAbn: boolean;
  minAbnYears: number | null;
  ruleDescription: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface FormState {
  incomeType: string;
  borrowerType: string;
  shadingPercentage: string;
  minYearsEmployed: string;
  requiresAbn: boolean;
  minAbnYears: string;
  ruleDescription: string;
  isActive: boolean;
}

export interface LenderIncomeShadingPanelProps {
  lenderId: string;
}

// Income type options (10 types per spec)
const INCOME_TYPE_OPTIONS = [
  { value: 'payg_salary', label: 'PAYG Salary' },
  { value: 'payg_wage', label: 'PAYG Wage' },
  { value: 'self_employed', label: 'Self Employed' },
  { value: 'rental', label: 'Rental' },
  { value: 'dividend', label: 'Dividend' },
  { value: 'trust_distribution', label: 'Trust Distribution' },
  { value: 'government_benefit', label: 'Government Benefit' },
  { value: 'superannuation', label: 'Superannuation' },
  { value: 'investment', label: 'Investment' },
  { value: 'other', label: 'Other' },
];

// Borrower type options (5 types per spec)
const BORROWER_TYPE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
  { value: 'trust', label: 'Trust' },
  { value: 'smsf', label: 'SMSF' },
];

const INCOME_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  INCOME_TYPE_OPTIONS.map(o => [o.value, o.label])
);

const BORROWER_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  BORROWER_TYPE_OPTIONS.map(o => [o.value, o.label])
);

const defaultFormState: FormState = {
  incomeType: '',
  borrowerType: 'all',
  shadingPercentage: '',
  minYearsEmployed: '',
  requiresAbn: false,
  minAbnYears: '',
  ruleDescription: '',
  isActive: true,
};

// Build conditions text from rule
function buildConditionsText(rule: IncomeShadingRule): string {
  const conditions: string[] = [];
  if (rule.minYearsEmployed != null && rule.minYearsEmployed > 0) {
    conditions.push(`Min ${rule.minYearsEmployed} years`);
  }
  if (rule.requiresAbn) {
    conditions.push('Requires ABN');
  }
  if (rule.minAbnYears != null && rule.minAbnYears > 0) {
    conditions.push(`ABN ${rule.minAbnYears}+ years`);
  }
  return conditions.length > 0 ? conditions.join(' \u2022 ') : '\u2014';
}

export const LenderIncomeShadingPanel: React.FC<LenderIncomeShadingPanelProps> = ({ lenderId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null);
  const [editingRule, setEditingRule] = useState<IncomeShadingRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<IncomeShadingRule | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [toggleFeedback, setToggleFeedback] = useState<string | null>(null);

  const apiPath = `/api/crm/lenders/${lenderId}/income-shading`;

  // Fetch rules
  const { data: rules = [], isLoading } = useQuery<IncomeShadingRule[]>({
    queryKey: [apiPath],
    enabled: !!lenderId,
  });

  // Sync form state when editing
  useEffect(() => {
    if (editingRule && modalMode === 'edit') {
      setFormState({
        incomeType: editingRule.incomeType,
        borrowerType: editingRule.borrowerType || 'all',
        shadingPercentage: String(editingRule.shadingPercentage),
        minYearsEmployed: editingRule.minYearsEmployed != null ? String(editingRule.minYearsEmployed) : '',
        requiresAbn: editingRule.requiresAbn || false,
        minAbnYears: editingRule.minAbnYears != null ? String(editingRule.minAbnYears) : '',
        ruleDescription: editingRule.ruleDescription || '',
        isActive: editingRule.isActive,
      });
    }
  }, [editingRule, modalMode]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', apiPath, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      handleCloseModal();
      toast({ title: 'Shading rule added', description: 'Income shading rule has been created' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to create shading rule', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const res = await apiRequest('PATCH', `${apiPath}/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      handleCloseModal();
      toast({ title: 'Shading rule updated', description: 'Income shading rule has been updated' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to update shading rule', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `${apiPath}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      setDeleteRule(null);
      toast({ title: 'Shading rule deleted', description: 'Income shading rule has been removed' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete shading rule', variant: 'destructive' });
    },
  });

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const res = await apiRequest('PATCH', `${apiPath}/${id}`, { isActive });
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [apiPath] });
      const msg = variables.isActive ? 'Rule activated' : 'Rule deactivated';
      setToggleFeedback(msg);
      setTimeout(() => setToggleFeedback(null), 5000);
    },
  });

  const handleOpenAdd = () => {
    setEditingRule(null);
    setFormState(defaultFormState);
    setModalMode('add');
  };

  const handleOpenEdit = (rule: IncomeShadingRule) => {
    setEditingRule(rule);
    setModalMode('edit');
  };

  const handleCloseModal = () => {
    setModalMode(null);
    setEditingRule(null);
    setFormState(defaultFormState);
  };

  const handleSave = async () => {
    const shadingPercentage = parseFloat(formState.shadingPercentage);
    if (isNaN(shadingPercentage)) return;

    const payload: any = {
      incomeType: formState.incomeType,
      borrowerType: formState.borrowerType,
      shadingPercentage,
      minYearsEmployed: formState.minYearsEmployed ? parseInt(formState.minYearsEmployed, 10) : null,
      requiresAbn: formState.requiresAbn,
      minAbnYears: formState.minAbnYears ? parseInt(formState.minAbnYears, 10) : null,
      ruleDescription: formState.ruleDescription || null,
      isActive: formState.isActive,
    };

    if (modalMode === 'edit' && editingRule) {
      await updateMutation.mutateAsync({ id: editingRule.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const modalTitle = modalMode === 'edit' ? 'Edit Shading Rule' : 'Add Shading Rule';
  const submitLabel = modalMode === 'edit' ? 'Save Changes' : 'Add Rule';

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="lender-income-shading-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="lender-income-shading-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Income Shading Rules</CardTitle>
            {rules.length > 0 && (
              <Button variant="outline" size="sm" onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-1" />
                Add Shading Rule
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            // Empty state
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Percent className="h-8 w-8 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">No income shading rules configured</p>
              <p className="text-xs text-muted-foreground mb-6">
                Default system rules will apply. Add lender-specific rules to override.
              </p>
              <Button onClick={handleOpenAdd}>
                <Plus className="h-4 w-4 mr-2" />
                Add Shading Rule
              </Button>
            </div>
          ) : (
            // Table view
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Income Type</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Borrower Type</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Shading %</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Conditions</th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground">Active</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((rule) => (
                    <tr
                      key={rule.id}
                      className={`border-b ${!rule.isActive ? 'opacity-50' : ''}`}
                    >
                      <td className="py-2 px-2">
                        <Badge variant="outline">
                          {INCOME_TYPE_LABELS[rule.incomeType] || rule.incomeType}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">
                        {BORROWER_TYPE_LABELS[rule.borrowerType] || rule.borrowerType || 'All'}
                      </td>
                      <td className="py-2 px-2 text-right font-medium">
                        {Number(rule.shadingPercentage)}%
                      </td>
                      <td className="py-2 px-2 text-xs text-muted-foreground">
                        {buildConditionsText(rule)}
                      </td>
                      <td className="py-2 px-2 text-center">
                        <Switch
                          checked={rule.isActive}
                          onCheckedChange={(checked) =>
                            toggleActiveMutation.mutate({ id: rule.id, isActive: checked })
                          }
                        />
                      </td>
                      <td className="py-2 px-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEdit(rule)}
                            aria-label="edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteRule(rule)}
                            aria-label="delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {toggleFeedback && (
                <p className="text-xs text-muted-foreground mt-2 text-right">{toggleFeedback}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={modalMode !== null} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modalTitle}</DialogTitle>
            <DialogDescription>
              {modalMode === 'edit'
                ? 'Update the shading rule details below'
                : 'Add a new income shading rule for this lender'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Income Type select */}
            <div className="space-y-2">
              <Label htmlFor="incomeType">Income Type</Label>
              <select
                id="incomeType"
                name="incomeType"
                value={formState.incomeType}
                onChange={(e) => setFormState(prev => ({ ...prev, incomeType: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="">Select Income Type</option>
                {INCOME_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Borrower Type select */}
            <div className="space-y-2">
              <Label htmlFor="borrowerType">Borrower Type</Label>
              <select
                id="borrowerType"
                name="borrowerType"
                value={formState.borrowerType}
                onChange={(e) => setFormState(prev => ({ ...prev, borrowerType: e.target.value }))}
                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                {BORROWER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Shading Percentage */}
            <div className="space-y-2">
              <Label htmlFor="shadingPercentage">Shading Percentage (%)</Label>
              <Input
                id="shadingPercentage"
                name="shadingPercentage"
                data-testid="shading-percentage-input"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formState.shadingPercentage}
                onChange={(e) => setFormState(prev => ({ ...prev, shadingPercentage: e.target.value }))}
                placeholder="e.g., 80"
              />
            </div>

            {/* Min Years Employed */}
            <div className="space-y-2">
              <Label htmlFor="minYearsEmployed">Min Years Employed</Label>
              <Input
                id="minYearsEmployed"
                name="minYearsEmployed"
                data-testid="min-years-employed-input"
                type="number"
                min="0"
                value={formState.minYearsEmployed}
                onChange={(e) => setFormState(prev => ({ ...prev, minYearsEmployed: e.target.value }))}
                placeholder="e.g., 2"
              />
            </div>

            {/* Requires ABN toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="requiresAbn">Requires ABN</Label>
              <Switch
                id="requiresAbn"
                data-testid="requires-abn-toggle"
                checked={formState.requiresAbn}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, requiresAbn: checked }))}
              />
            </div>

            {/* Min ABN Years (conditional) */}
            {formState.requiresAbn && (
              <div className="space-y-2">
                <Label htmlFor="minAbnYears">Min ABN Years</Label>
                <Input
                  id="minAbnYears"
                  name="minAbnYears"
                  type="number"
                  min="0"
                  value={formState.minAbnYears}
                  onChange={(e) => setFormState(prev => ({ ...prev, minAbnYears: e.target.value }))}
                  placeholder="e.g., 2"
                />
              </div>
            )}

            {/* Rule Description */}
            <div className="space-y-2">
              <Label htmlFor="ruleDescription">Rule Description</Label>
              <Input
                id="ruleDescription"
                name="ruleDescription"
                data-testid="rule-description-input"
                value={formState.ruleDescription}
                onChange={(e) => setFormState(prev => ({ ...prev, ruleDescription: e.target.value }))}
                placeholder="e.g., Self-employed income at 80%"
              />
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="isActive">Active</Label>
              <Switch
                id="isActive"
                data-testid="is-active-toggle"
                checked={formState.isActive}
                onCheckedChange={(checked) => setFormState(prev => ({ ...prev, isActive: checked }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteRule} onOpenChange={(open) => !open && setDeleteRule(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shading Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Remove shading rule for {deleteRule ? (INCOME_TYPE_LABELS[deleteRule.incomeType] || deleteRule.incomeType) : ''}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteRule && deleteMutation.mutate(deleteRule.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LenderIncomeShadingPanel;
